import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from 'shared/api';
import GreenCheck from 'shared/assets/icons/green_check.svg';
import { ButtonCustom } from 'shared/ui/button-custom';
import { TextInputSmall } from 'shared/ui/text-input-small';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import styles from './agents-admin.module.scss';

const getPageTitle = (value?: string) => {
  switch (value) {
    case 'agents':
      return 'Список агентов TLC Group';
    case 'suppliers':
      return 'Список поставщиков TLC Group';
    case 'leasings':
      return 'Список лизинговых компаний TLC Group';
    case 'clients':
      return 'Список клиентов TLC Group';
    default:
      return 'Список пользователей TLC Group';
  }
};

export const AgentsAdmin = () => {
  const { type } = useParams();
  const navigate = useNavigate();

  const [agentsArr, setAgentsArr] = useState<any[]>([]);

  const [open, setOpen] = useState(false);
  const [openAcc, setOpenAcc] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [selectedAgentAcc, setSelectedAgentAcc] = useState<any>(null);
  const [share, setShare] = useState('');
  const [shareAcc, setShareAcc] = useState('');
  const [shareErr, setShareErr] = useState(false);
  const [shareErrAcc, setShareErrAcc] = useState(false);

  const getAgentsArr = async () => {
    try {
      if (!type) throw Error('user type is required');

      const preparedType = type.slice(0, -1);

      const response = await apiClient.get(`/agents/find?user_type=${preparedType}`);
      const data = Array.isArray(response.data.data) ? response.data.data : [];

      const sorted = data.sort((a: any, b: any) => {
        if (a.confirmed === b.confirmed) {
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
        return a.confirmed ? 1 : -1;
      });

      setAgentsArr(sorted);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    getAgentsArr();
  }, [type]);

  const setConfirm = async (agent: any) => {
    await apiClient.post('/new-agent/confirm', {
      id: agent.id,
      share: parseFloat(shareAcc),
    });
    getAgentsArr();
    handleCloseAcc();
  };

  const handleAgentAccept = async (agent: any) => {
    if (type === 'agents') {
      setSelectedAgentAcc({ ...agent });
      setOpenAcc(true);
    } else {
      setConfirm(agent);
    }
  };

  const handleSetShareAcc = async () => {
    if (!selectedAgentAcc || !shareAcc) {
      setShareErrAcc(true);
      return;
    }
    try {
      if (!shareErrAcc) {
        await apiClient.post('/new-agent/confirm', {
          id: selectedAgentAcc.id,
          share: parseFloat(shareAcc),
        });
        getAgentsArr();
        handleCloseAcc();
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleAgentClick = (agent: any) => {
    navigate(`/dashboard/admin/users/${type}/${agent.id}`);

    if (!agent.confirmed) return;
    setSelectedAgent({ ...agent });
    setShare(agent.share?.toString() || '');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedAgent(null);
    setShare('');
    setShareErr(false);
  };
  const handleCloseAcc = () => {
    setOpenAcc(false);
    setSelectedAgentAcc(null);
    setShareAcc('');
    setShareErrAcc(false);
  };

  const handleSetShare = async () => {
    if (!selectedAgent || !share) {
      setShareErr(true);
      return;
    }

    try {
      await apiClient.post('/new-agent/share', {
        email: selectedAgent.email,
        share: parseFloat(share),
      });
      getAgentsArr();
      handleClose();
    } catch (e) {
      console.log(e);
    }
  };

  const handleChangeShareValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setShare(value);
    setShareErr(!value || Number(value) <= 0 || Number(value) > 1);
  };
  const handleChangeShareValueAcc = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setShareAcc(value);
    setShareErrAcc(!value || Number(value) <= 0 || Number(value) > 1);
  };

  const point = (agent: any) => {
    return agent ? 'pointer' : 'default';
  };

  return (
    <Box className={styles.agentsPage}>
      <DashboardHeaderBackLink link={'/dashboard/admin/last'} title={getPageTitle(type)} />
      <Box>
        <TableContainer component={Box} className={styles.tableContainer}>
          <Table className={styles.table} sx={{ minWidth: 1070 }} aria-label={getPageTitle(type)}>
            <TableHead className={styles.tableHead}>
              <TableRow className={styles.tableHeadRow}>
                <TableCell className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell}`}>
                  ФИО
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  E-mail
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  Телефон
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  Доля
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCellLast} ${styles.tableHeadCell}`}
                  align='right'
                />
              </TableRow>
            </TableHead>
            <TableBody className={styles.tableBody}>
              {agentsArr.map((agent) => (
                <TableRow
                  key={agent.id}
                  className={styles.tableBodyRow}
                  onClick={() => handleAgentClick(agent)}
                  sx={{
                    cursor: `${point(agent.confirmed)}`,
                    '& td, & th': { border: 0 },
                    marginTop: '10px',
                  }}
                >
                  <TableCell
                    className={`${styles.tableBodyCellFirst} ${styles.tableBodyCell}`}
                    component='th'
                    scope='row'
                  >
                    {agent.bio}
                  </TableCell>
                  <TableCell
                    className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                    align='left'
                  >
                    {agent.email}
                  </TableCell>
                  <TableCell
                    className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                    align='left'
                  >
                    {agent.phone}
                  </TableCell>
                  <TableCell
                    className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                    align='left'
                  >
                    {agent.user_type === 'agent' && agent.share ? agent.share : '-'}
                  </TableCell>
                  <TableCell
                    className={`${styles.tableBodyCellLast} ${styles.tableBodyCell}`}
                    align='right'
                  >
                    {!agent.confirmed && type !== 'clients' ? (
                      <Box
                        className={styles.greenBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAgentAccept(agent);
                        }}
                      >
                        <ButtonCustom>Подтвердить</ButtonCustom>
                      </Box>
                    ) : (
                      <SvgIcon
                        className={styles.greenCheck}
                        component={GreenCheck}
                        inheritViewBox
                        sx={{ fontSize: '18px' }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        sx={{
          '.MuiDialog-paper': {
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '350px',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='alert-dialog-title'>
          <span>Введите долю агента</span>
        </DialogTitle>
        <DialogContent className={styles.modalDescriptionWr}>
          <DialogContentText className={styles.modalDescription} id='alert-dialog-description'>
            <Box className={styles.offerDesc}>
              <span className={styles.appDescTitle}>Доля</span>
              <TextInputSmall
                onChange={handleChangeShareValue}
                value={share}
                error={shareErr}
                required
                name={'share'}
                type={'number'}
                placeholder={'Введите долю от 0 до 1'}
              />
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          <ButtonCustom className={styles.modalButtonCancel} onClick={handleClose}>
            Отмена
          </ButtonCustom>
          <ButtonCustom className={styles.modalButton} onClick={handleSetShare}>
            Подтвердить
          </ButtonCustom>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAcc}
        onClose={handleCloseAcc}
        aria-labelledby='alert-dialog-titleAcc'
        aria-describedby='alert-dialog-descriptionAcc'
        sx={{
          '.MuiDialog-paper': {
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '350px',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='alert-dialog-title'>
          <span>Введите долю агента</span>
        </DialogTitle>
        <DialogContent className={styles.modalDescriptionWr}>
          <DialogContentText className={styles.modalDescription} id='alert-dialog-descriptionAcc'>
            <Box className={styles.offerDesc}>
              <span className={styles.appDescTitle}>Доля</span>
              <TextInputSmall
                onChange={handleChangeShareValueAcc}
                value={shareAcc}
                error={shareErrAcc}
                required
                name={'shareAcc'}
                type={'number'}
                placeholder={'Введите долю от 0 до 1'}
              />
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          <ButtonCustom className={styles.modalButtonCancel} onClick={handleCloseAcc}>
            Отмена
          </ButtonCustom>
          <ButtonCustom className={styles.modalButton} onClick={handleSetShareAcc}>
            Подтвердить
          </ButtonCustom>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
